import { Component, OnInit } from '@angular/core';
import { Router, NavigationExtras } from '@angular/router';
import {
  TableCell,
  TableData,
  TableHeader
} from '@dxo/ng-swift-ui/lib/components/table/components/table-base/table.types';
import {
  ApiResponseOfPolicySearchResultsResponse,
  PolicyClient,
  SearchCategory,
  SearchSortField,
  SearchSortDirection
} from '@benefitsui/app/api/benefits-clients';
import { UserContextService } from '@benefitsui/app/services/user-context/user-context.service';
import { UserContext } from '@benefitsui/app/services/user-context/user-context.types';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PolicyInfo } from './list-policies-page.types';
import { AuthenticationService } from '@benefitsui/app/services/auth/authentication.service';

@Component({
  selector: 'app-list-policies-page',
  templateUrl: './list-policies-page.component.html',
  styleUrls: ['./list-policies-page.component.scss']
})
export class ListPoliciesPageComponent implements OnInit {
  //constants
  PolicyTitle = 'Search for policies';
  GroupTitle = 'Search for groups';
  PolicyHolder = 'Policy holder';
  PolicyBodyHeader = 'All policies';
  PolicyNumber = 'Policy number';
  GroupName = 'Group name';
  GroupNumber = 'Group number';
  GroupBodyHeader = 'All groups';
  data$!: Observable<TableData>;
  hasPolicies$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  policyNumbers$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  userFullName: string;
  userContext: UserContext;
  bodyHeader = this.PolicyBodyHeader;
  isPolicy = true;
  pageTitle = this.PolicyTitle;
  tableName = this.PolicyHolder;
  tableNumber = this.PolicyNumber;
  hasTermsRecords = false;
  termsConditionsAccepted = false;
  electronicConsentAccepted = false;
  showListPage = false;
  loading = true;
  showPopUpPage = false;
  hasOnlyOnePolicy = false;
  tokenForOverview: string = '';

  constructor(
    private router: Router,
    private userContextService: UserContextService,
    private policyClient: PolicyClient,
    private authService: AuthenticationService
  ) {
    this.userContext = this.userContextService.get();
    this.userFullName = this.userContext.currentUserFullName;
  }

  ngOnInit(): void {
    this.data$ = this.policyClient
      .search({
        category: SearchCategory.GroupId,
        criteria: '',
        page: 1,
        pageSize: 10,
        sortField: SearchSortField.Name,
        sortDirection: SearchSortDirection.Ascending
      })
      .pipe(map(this.transformData, this));

    // async pipes in the view may not fire, so we gotta subscribe
    // explicitly here to ensure data is being fetched regardless
    this.data$.subscribe();
    this.isGroupsPage(this.userContext);
  }

  showTermsConditionsElectronicPopup(input: UserContext) {
    if (input.symClass.toLowerCase() === 'employee' || input.symClass.toLowerCase() === 'distributor') {
      this.showListPage = true;
      this.showPopUpPage = false;
    } else {
        this.hasTermsRecords= this.userContext.termConditionConsentInfo?.hasRecords ?? false;
        this.termsConditionsAccepted = this.userContext.termConditionConsentInfo?.termsConditionsAccepted ?? false;
        this.electronicConsentAccepted = this.userContext.termConditionConsentInfo?.electronicConsentAccepted ?? false;        
        const hasAccepted = this.hasTermsRecords && this.termsConditionsAccepted && this.electronicConsentAccepted;                  
        this.showPopUpPage = !hasAccepted;
    }
  }

  doneAcceptence() {
    this.showPopUpPage = false;
    if(this.hasOnlyOnePolicy && this.tokenForOverview !== ''){
      this.loading = true;
      this.routeToOverview(this.tokenForOverview);      
    }
  }
  
  logout() {
    this.tokenForOverview = '';
    this.showListPage = false;
    this.showPopUpPage = false;
    this.authService.logout();
  }

  isGroupsPage(input: UserContext) {
    const userType = input.symClass.toLowerCase();
    const employeeInfo = input.symEmployeeInfo;
    const isSymEmpl = userType === 'employee';
    const isBasic = employeeInfo?.isBasic;
    const isBasicSymEmp = isSymEmpl && isBasic;
    const isBroker = userType === 'distributor';
    if (isBasicSymEmp || isBroker) {
      this.bodyHeader = this.GroupBodyHeader;
      this.isPolicy = false;
      this.pageTitle = this.GroupTitle;
      this.tableName = this.GroupName;
      this.tableNumber = this.GroupNumber;
    }
  }

  routeToOverview(token: string | undefined) {
    const queryParams: NavigationExtras = {
      queryParams: { token }
    };
    if(token !== undefined){
      this.router.navigate(['/benefits/dashboard'], queryParams);
    }
    else{
      this.router.navigate(['/benefits/search'], queryParams);
    }
  }

  private transformData(response: ApiResponseOfPolicySearchResultsResponse): TableData {
    const responseData = response.data?.searchResults ?? [];
    this.showTermsConditionsElectronicPopup(this.userContext);
    if (responseData.length == 1) {
      this.hasOnlyOnePolicy = true;
      this.tokenForOverview = responseData?.[0]?.policyToken ?? '';      
      if(!this.showPopUpPage && this.tokenForOverview !== ''){
        this.routeToOverview(this.tokenForOverview);
        // returning a "blank" response to keep typescript happy.
        // the routing call directly above is gonna redirect us anyway
        return { headers: [], cells: [] };
      }
    }

    const policies: PolicyInfo[] = responseData.map((rsp) => {
      const success = rsp;
      const returnPolicies = {
        policyholder: success.policyholder,
        policyNumber: success.policyId,
        policyToken: success.policyToken,
        groupId: success.groupId,
        groupName: success.groupName
      };

      return returnPolicies;
    });

    const headers: TableHeader[] = [{ text: this.tableName, width: '60%' }, { text: this.tableNumber }];
    const cells: TableCell[][] = [];

    this.hasPolicies$.next(!!policies.length);
    this.policyNumbers$.next(policies.length);

    policies?.forEach((policy) => {
      const name = this.isPolicy ? policy.policyholder : policy.groupName;
      const number = this.isPolicy ? policy.policyNumber : policy.groupId;

      cells.push([
        {
          link: {
            href: `benefits/dashboard?token=${policy.policyToken}`,
            sameTab: true,
            dataset: {
              e2e: 'CustomerLandingPage_ViewPolicyButton'
            }
          },
          display: name
        },
        {
          link: {
            href: `benefits/dashboard?token=${policy.policyToken}`,
            sameTab: true,
            dataset: {
              e2e: 'CustomerLandingPage_ViewPolicyButton'
            }
          },
          display: number
        }
      ]);
    });

    this.loading = false;
    return { headers, cells };
  }
}
